// Icon — single-source inline-SVG pictograms (이모지 금지 규칙의 핵심).
//
// design_handoff_kuun_ui_unification/kuun-shared.jsx 의 `Icon` + `K_ICONS` 를
// 그대로 포팅. lucide-style 24px viewBox, stroke=currentColor, round cap/join.
// 멀티 path 는 " M" 기준으로 쪼개 여러 <path> 로 렌더(결정적).
//
// 사용: <Icon name="bell" size={20} color="var(--fg-muted)" />
// 새 픽토그램이 필요하면 K_ICONS 에 같은 규칙(24 viewBox path)으로 추가.
//
// 어떤 개념에 어떤 아이콘을 쓰는지는 docs/design/icon-vocabulary.md 가 정본이다.
// 새 아이콘을 넣기 전에 그 표에서 기존 개념과 뜻이 겹치지 않는지 먼저 확인할 것
// (같은 그림이 서로 다른 뜻으로 쓰이면 화면 전체의 신뢰가 깨진다).

import type { CSSProperties } from 'react';

export const K_ICONS = {
  grid: 'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z',
  inbox:
    'M22 12h-6l-2 3h-4l-2-3H2 M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z',
  calendar:
    'M8 2v4M16 2v4M3 10h18 M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z',
  bot: 'M12 8V4H8 M2 14h2M20 14h2M15 13v2M9 13v2 M4 8h16a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2v-8a2 2 0 012-2z',
  factory:
    'M2 20a1 1 0 001 1h18a1 1 0 001-1V8l-7 5V8l-7 5V4a1 1 0 00-1-1H3a1 1 0 00-1 1zM6 18h2M14 18h2',
  qr: 'M3 3h6v6H3zM15 3h6v6h-6zM3 15h6v6H3z M15 15h2v2h-2zM19 15h2v2h-2zM15 19h2v2h-2zM19 19h2v2h-2z',
  search: 'M11 19a8 8 0 100-16 8 8 0 000 16zM21 21l-4.3-4.3',
  bell: 'M6 8a6 6 0 0112 0c0 7 3 9 3 9H3s3-2 3-9 M10.3 21a1.94 1.94 0 003.4 0',
  // 경고 어휘: alert(삼각형) = 모든 일반 경고·주의·검증 실패의 기본값.
  // siren 은 SLA 초과·지연 전용(아래 참조) — 둘을 같은 심각도에 섞어 쓰지 말 것.
  alert:
    'M12 9v4M12 17h.01 M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z',
  check: 'M20 6L9 17l-5-5',
  'check-circle': 'M22 11.08V12a10 10 0 11-5.93-9.14 M22 4L12 14.01l-3-3',
  'chev-right': 'M9 18l6-6-6-6',
  'chev-down': 'M6 9l6 6 6-6',
  'chev-left': 'M15 18l-6-6 6-6',
  'chev-up': 'M18 15l-6-6-6 6',
  link: 'M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71 M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71',
  more: 'M12 13a1 1 0 100-2 1 1 0 000 2zM19 13a1 1 0 100-2 1 1 0 000 2zM5 13a1 1 0 100-2 1 1 0 000 2z',
  plus: 'M12 5v14M5 12h14',
  camera:
    'M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z M12 17a4 4 0 100-8 4 4 0 000 8z',
  pen: 'M12 19l7-7 3 3-7 7-3-3z M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z M2 2l7.586 7.586 M11 13a2 2 0 100-4 2 2 0 000 4z',
  signature: 'M3 17c3 0 4-8 6-8s2 6 4 6 2-4 4-4 M3 21h18',
  building: 'M3 21h18M6 21V7l6-4 6 4v14 M9 9h.01M9 12h.01M9 15h.01M15 9h.01M15 12h.01M15 15h.01',
  store: 'M2 7l1-4h18l1 4 M4 7v13a1 1 0 001 1h14a1 1 0 001-1V7 M2 7h20 M9 21v-6h6v6',
  wrench: 'M14.7 6.3a4 4 0 00-5.6 5.3L3 18l3 3 6.4-6.1a4 4 0 005.3-5.6l-2.7 2.7-2.6-2.6z',
  truck:
    'M1 3h15v13H1zM16 8h4l3 3v5h-7V8z M5.5 19a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM18.5 19a2.5 2.5 0 100-5 2.5 2.5 0 000 5z',
  package:
    'M16.5 9.4L7.5 4.21 M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12',
  sparkles:
    'M12 3l1.9 5.8a2 2 0 001.3 1.3L21 12l-5.8 1.9a2 2 0 00-1.3 1.3L12 21l-1.9-5.8a2 2 0 00-1.3-1.3L3 12l5.8-1.9a2 2 0 001.3-1.3L12 3z',
  file: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6 M16 13H8M16 17H8M10 9H8',
  users:
    'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M9 11a4 4 0 100-8 4 4 0 000 8z M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75',
  eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z M12 15a3 3 0 100-6 3 3 0 000 6z',
  'arrow-right': 'M5 12h14M12 5l7 7-7 7',
  // 뒤로가기 전용 — 예전엔 arrow-right 를 CSS rotate(180deg) 로 뒤집어 썼다
  // (화살촉 stroke join 이 미묘하게 어긋나고, 검색으로 찾기도 어려웠다).
  'arrow-left': 'M19 12H5M12 19l-7-7 7-7',
  clock: 'M12 22a10 10 0 100-20 10 10 0 000 20z M12 6v6l4 2',
  'map-pin': 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z M12 13a3 3 0 100-6 3 3 0 000 6z',
  phone:
    'M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z',
  x: 'M18 6L6 18M6 6l12 12',
  menu: 'M3 12h18M3 6h18M3 18h18',
  refresh: 'M23 4v6h-6M1 20v-6h6 M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15',
  // undo/redo 는 refresh 와 구분돼야 한다 — 예전엔 둘 다 refresh 를 써서
  // 실행 취소/다시 실행 버튼이 같은 그림이었다.
  undo: 'M9 14L4 9l5-5 M4 9h11a5 5 0 010 10h-4',
  redo: 'M15 14l5-5-5-5 M20 9H9a5 5 0 000 10h4',
  settings:
    'M12 15a3 3 0 100-6 3 3 0 000 6z M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z',
  battery: 'M2 7h16a1 1 0 011 1v8a1 1 0 01-1 1H2a1 1 0 01-1-1V8a1 1 0 011-1z M23 11v2',
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  filter: 'M22 3H2l8 9.46V19l4 2v-8.54z',
  download: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M7 10l5 5 5-5 M12 15V3',
  trash: 'M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2',
  scroll: 'M8 21h12a2 2 0 002-2v-2H10v2a2 2 0 11-4 0V5a2 2 0 10-4 0v3h4 M19 17V5a2 2 0 00-2-2H4',
  smile: 'M12 2a10 10 0 100 20 10 10 0 000-20z M8 14s1.5 2 4 2 4-2 4-2 M9 9h.01 M15 9h.01',
  // SLA 초과·지연 전용. 그 밖의 경고는 전부 alert.
  siren:
    'M7 18v-6a5 5 0 0110 0v6 M5 21h14 M12 1v2 M4.2 5.2l1.4 1.4 M19.8 5.2l-1.4 1.4 M2 12h2 M20 12h2',
  // ── 아래는 icon-vocabulary 정본화(§9)에서 개념 충돌을 풀려고 추가한 것들.
  // 로더(로봇 팔) — 종전엔 wrench 를 유지보수와 공유했다.
  'robot-arm': 'M3 21h8 M7 21v-8 M7 13l6-4 M13 9l3-2 M14 2h7v5h-7z',
  // 부품 — 종전엔 wrench 를 유지보수·로더와 공유했다.
  component:
    'M5.5 8.5L9 12l-3.5 3.5L2 12z M12 2l3.5 3.5L12 9L8.5 5.5z M18.5 8.5L22 12l-3.5 3.5L15 12z M12 15l3.5 3.5L12 22l-3.5-3.5z',
  // 감사 로그·이력 — 종전엔 scroll 을 ECN 과 공유했다.
  history: 'M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8 M3 3v5h5 M12 7v5l4 2',
  // 교체(swap) — 종전엔 refresh 를 데이터 새로고침과 공유했다.
  swap: 'M8 3L4 7l4 4 M4 7h16 M16 21l4-4-4-4 M20 17H4',
  mail: 'M22 6l-10 7L2 6 M4 4h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2z',
  // 개인정보(DSAR) — 종전엔 file 을 계약·첨부·문서와 공유했다.
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  // 로더 리비전(적층 = 판올림) — 종전엔 list 를 재고·셀프체크와 공유했다.
  layers: 'M12 2L2 7l10 5 10-5z M2 17l10 5 10-5 M2 12l10 5 10-5',
  // 2026-10-27 — 해외 설치 카드(오너 "해외 설치는 지구본 모양이 낫겠는걸").
  globe:
    'M12 2a10 10 0 100 20 10 10 0 000-20z M2 12h20 M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z',
  // 보류(멈춤) — 2026-10-23 데모-계약 전환 Status.
  pause: 'M6 4h4v16H6z M14 4h4v16h-4z',
  // 중복(같은 문의가 또 들어옴) — 2026-10-23 인바운드 현황.
  copy: 'M11 9h9a2 2 0 012 2v9a2 2 0 01-2 2h-9a2 2 0 01-2-2v-9a2 2 0 012-2z M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1',
  // 도입 문의 '연락 여부'(2026-10-23) — 부재(안 받음) · 콜백(되걸려 옴) · 카톡 회신.
  'phone-off':
    'M10.68 13.31a16 16 0 003.41 2.6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7 2 2 0 011.72 2v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.42 19.42 0 01-3.33-2.67 M8.01 10.97a19.79 19.79 0 01-3.07-8.63A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91 M22 2L2 22',
  'phone-in':
    'M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z M16 2v6h6 M22 2l-6 6',
  message:
    'M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z',
  // 셀프체크(점검 항목) — 종전엔 list 를 재고와 공유했다.
  'list-check': 'M3 17l2 2 4-4 M3 7l2 2 4-4 M13 6h8 M13 12h8 M13 18h8',
} as const;

export type IconName = keyof typeof K_ICONS;

interface IconProps {
  name: IconName;
  size?: number;
  /** stroke color — use a token, e.g. "var(--fg-muted)". Defaults to currentColor. */
  color?: string;
  strokeWidth?: number;
  style?: CSSProperties;
  'aria-hidden'?: boolean;
  title?: string;
}

export function Icon({
  name,
  size = 18,
  color = 'currentColor',
  strokeWidth = 1.75,
  style,
  title,
  'aria-hidden': ariaHidden = true,
}: IconProps): JSX.Element {
  const d = K_ICONS[name] ?? '';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : ariaHidden}
      style={{ flexShrink: 0, display: 'block', ...style }}
    >
      {title && <title>{title}</title>}
      {d.split(' M').map((seg, i) => (
        <path key={i} d={i === 0 ? seg : 'M' + seg} />
      ))}
    </svg>
  );
}
